/**
 * Read the `exp` claim off a JWT WITHOUT verifying it (CLM-65, D-65-3).
 *
 * This exists for one display decision: when the gateway answers 401, was the
 * token the client attached already past its expiry (sign in again) or still
 * good (the API rejected it structurally — wrong issuer, wrong audience — and
 * signing in again will not help)? The first production sign-in produced the
 * second case and the shell called it "Session expired", which sent the user
 * round a loop that could not fix a configuration mismatch.
 *
 * No signature check, on purpose: the SPA holds no key and trusts nothing it
 * reads here for authorisation. The gateway verifies the token; this only
 * decides which sentence to show. A token that cannot be decoded reads as
 * `null`, which the classifier treats like "no token" — today's copy.
 */
export function decodeJwtExp(token: string | null | undefined): number | null {
  if (typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const payload = parts[1];
  if (payload === undefined || payload === '') return null;

  try {
    const claims: unknown = JSON.parse(decodeBase64Url(payload));
    if (typeof claims !== 'object' || claims === null) return null;
    const exp = (claims as Record<string, unknown>)['exp'];
    return typeof exp === 'number' && Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
}

function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  // atob yields a byte string; decode it as UTF-8 so a non-ASCII claim value
  // elsewhere in the payload cannot make JSON.parse trip on a stray byte.
  const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
